import {announce} from '@github-ui/aria-live'
import {XIcon} from '@primer/octicons-react'
import {Button, IconButton, Popover, useOnEscapePress} from '@primer/react'
import {clsx} from 'clsx'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'
import {createPortal} from 'react-dom'

import {isVisibleOnboardingAnchor} from './use-onboarding-anchor'
import styles from './CopilotOnboardingPopover.module.css'

interface Position {
  left: number
  top: number
  width: number
  height: number
  scrollHeight?: number
  caret: number
  above: boolean
  visible: boolean
}

interface CopilotOnboardingPopoverProps {
  anchor: HTMLButtonElement | HTMLAnchorElement
  title: string
  description: string
  onClose: () => void
  closeLabel?: string
  appearance?: 'default' | 'announcement'
  media?: ReactNode | ((shown: boolean) => ReactNode)
  step?: {index: number; count: number; onPrevious?: () => void; onNext: () => void}
  actions?: Array<{label: string; onClick: () => void; variant?: 'default' | 'primary'}>
  focusRequested?: boolean
  focusKey?: number
  scope?: Element | null
  escapeScope?: () => HTMLElement | null
  portalSlot: HTMLElement
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  onVisibilityChange?: (visible: boolean) => void
  onFocusChange?: (focused: boolean) => void
}

function hasOpenOnboardingTooltip(anchor: HTMLElement, popover: HTMLElement | null) {
  if (
    popover?.querySelector('[data-component="Tooltip"]:popover-open') ||
    anchor.querySelector('[data-component="Tooltip"]:popover-open')
  )
    return true
  return Array.from(anchor.ownerDocument.querySelectorAll('[data-component="Tooltip"]:popover-open')).some(tooltip => {
    if (!tooltip.id) return false
    return ['aria-describedby', 'aria-labelledby'].some(attribute =>
      anchor.getAttribute(attribute)?.split(/\s+/).includes(tooltip.id),
    )
  })
}

function isInsideNestedOverlay(target: Element, popover: HTMLElement, host?: HTMLElement | null) {
  for (let node: Element | null = target; node && node !== host; node = node.parentElement) {
    if (
      node !== popover &&
      (node.getAttribute('aria-expanded') === 'true' ||
        node.matches('dialog[open], [aria-modal="true"], details[open], [role="menu"], [role="listbox"]'))
    )
      return true
  }
  return false
}

function getPlacementBounds(portalSlot: HTMLElement, clientWidth: number, clientHeight: number) {
  let left = 16
  let right = clientWidth - 16
  let top = 16
  let bottom = clientHeight - 16
  let originLeft = 0
  let originTop = 0
  let scaleX = 1
  let scaleY = 1
  let hasContainingBlock = false
  const clippingAncestors: HTMLElement[] = []
  const ancestors: HTMLElement[] = []
  for (
    let node = portalSlot.parentElement;
    node && node !== portalSlot.ownerDocument.documentElement;
    node = node.parentElement
  ) {
    ancestors.push(node)
    const style = getComputedStyle(node)
    if (
      !hasContainingBlock &&
      (style.transform !== 'none' ||
        (style.translate || 'none') !== 'none' ||
        (style.rotate || 'none') !== 'none' ||
        (style.scale || 'none') !== 'none' ||
        style.filter !== 'none' ||
        (style.backdropFilter || 'none') !== 'none' ||
        style.perspective !== 'none' ||
        /transform|translate|rotate|scale|perspective|filter/.test(style.willChange) ||
        style.contentVisibility === 'auto' ||
        /layout|paint|content|strict/.test(style.contain))
    ) {
      const rect = node.getBoundingClientRect()
      scaleX = node.offsetWidth ? rect.width / node.offsetWidth : 1
      scaleY = node.offsetHeight ? rect.height / node.offsetHeight : 1
      originLeft = rect.left + (node.clientLeft - node.scrollLeft) * scaleX
      originTop = rect.top + (node.clientTop - node.scrollTop) * scaleY
      hasContainingBlock = true
    }
    const clips =
      ['hidden', 'clip', 'auto', 'scroll'].includes(style.overflowX) ||
      ['hidden', 'clip', 'auto', 'scroll'].includes(style.overflowY) ||
      /paint|content|strict/.test(style.contain) ||
      style.contentVisibility === 'auto'
    if (clips) {
      clippingAncestors.push(node)
      if (hasContainingBlock) {
        const rect = node.getBoundingClientRect()
        left = Math.max(left, rect.left + 8)
        right = Math.min(right, rect.right - 8)
        top = Math.max(top, rect.top + 8)
        bottom = Math.min(bottom, rect.bottom - 8)
      }
    }
  }
  return {left, right, top, bottom, originLeft, originTop, scaleX, scaleY, clippingAncestors, ancestors}
}

function VisiblePopoverEscapeHandler({
  anchor,
  popover,
  escapeScope,
  onClose,
  restoreFocus,
}: {
  anchor: HTMLButtonElement | HTMLAnchorElement
  popover: RefObject<HTMLDivElement | null>
  escapeScope?: () => HTMLElement | null
  onClose: () => void
  restoreFocus: () => void
}) {
  useOnEscapePress(
    event => {
      const doc = anchor.ownerDocument
      const active = doc.activeElement
      const target =
        event.target instanceof Element && event.target !== doc.body && event.target !== doc.documentElement
          ? event.target
          : active instanceof Element
            ? active
            : event.target
      if (!(target instanceof Element) || !popover.current) return
      const host = escapeScope?.()
      if (
        !popover.current.contains(target) &&
        !anchor.contains(target) &&
        target !== doc.body &&
        target !== doc.documentElement &&
        !host?.contains(target)
      )
        return
      if (anchor.getAttribute('aria-expanded') === 'true' || isInsideNestedOverlay(target, popover.current, host))
        return
      if (doc.querySelector('[data-component="AnchoredOverlay"]') || hasOpenOnboardingTooltip(anchor, popover.current))
        return
      event.preventDefault()
      restoreFocus()
      onClose()
    },
    [anchor, escapeScope, onClose, popover, restoreFocus],
  )
  return null
}

export function CopilotOnboardingPopover({
  anchor,
  title,
  description,
  onClose,
  closeLabel = 'Close tour',
  appearance = 'default',
  media,
  step,
  actions,
  focusRequested = false,
  focusKey = 0,
  scope,
  escapeScope,
  portalSlot,
  textAreaRef,
  onVisibilityChange,
  onFocusChange,
}: CopilotOnboardingPopoverProps) {
  const headingId = useId()
  const descriptionId = useId()
  const counterId = useId()
  const primaryRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const scrollableRef = useRef<HTMLDivElement>(null)
  const focusReturnRef = useRef<HTMLElement | null>(null)
  const previousAnchorRef = useRef(anchor)
  const previousRef = useRef<HTMLButtonElement>(null)
  const stepFocusRef = useRef<'previous' | 'primary' | null>(null)
  const previousPortalSlotRef = useRef(portalSlot)
  const announcedStepRef = useRef<string | null>(null)
  const focusedStepRef = useRef<string | null>(null)
  const focusedInsideRef = useRef(false)
  const [position, setPosition] = useState<Position | null>(null)
  const shown = position?.visible === true
  const anchorIntersectingRef = useRef(true)

  useEffect(() => {
    anchorIntersectingRef.current = true
  }, [anchor])

  useEffect(() => {
    let frame: number | null = null
    let disposed = false
    let layoutObserver: IntersectionObserver | null = null
    const update = () => {
      frame = null
      if (disposed) return
      const {clientWidth, clientHeight} = anchor.ownerDocument.documentElement
      const bounds = getPlacementBounds(portalSlot, clientWidth, clientHeight)
      const visible =
        anchorIntersectingRef.current &&
        isVisibleOnboardingAnchor(anchor, scope) &&
        anchor.getAttribute('aria-expanded') !== 'true' &&
        bounds.right > bounds.left &&
        bounds.bottom > bounds.top
      const rect = anchor.getBoundingClientRect()
      const scrollable = scrollableRef.current
      const chromeHeight =
        (popoverRef.current?.getBoundingClientRect().height ?? 0) - (scrollable?.getBoundingClientRect().height ?? 0)
      const contentHeight = (scrollable?.scrollHeight ?? 0) * bounds.scaleY
      const availableHeight = Math.max(0, bounds.bottom - bounds.top)
      const scrollHeight =
        contentHeight + chromeHeight > availableHeight + 1 ? Math.max(0, availableHeight - chromeHeight) : undefined
      const height = (scrollHeight ?? contentHeight) + chromeHeight
      const width = Math.min(320, Math.max(0, bounds.right - bounds.left))
      const left = Math.max(bounds.left, Math.min(rect.left + rect.width / 2 - width / 2, bounds.right - width))
      const spaceBelow = bounds.bottom - rect.bottom - 12
      const spaceAbove = rect.top - bounds.top - 12
      const above = spaceBelow < height && spaceAbove > spaceBelow
      const next = {
        left: (left - bounds.originLeft) / bounds.scaleX,
        top:
          ((above
            ? Math.min(bounds.bottom, Math.max(bounds.top + height, rect.top - 12))
            : Math.max(bounds.top, Math.min(rect.bottom + 12, bounds.bottom - height))) -
            bounds.originTop) /
          bounds.scaleY,
        width: width / bounds.scaleX,
        height: height / bounds.scaleY,
        scrollHeight: scrollHeight === undefined ? undefined : scrollHeight / bounds.scaleY,
        caret: Math.max(24, Math.min((rect.left + rect.width / 2 - left) / bounds.scaleX, width / bounds.scaleX - 24)),
        above,
        visible,
      }
      setPosition(previous =>
        previous &&
        previous.left === next.left &&
        previous.top === next.top &&
        previous.width === next.width &&
        previous.height === next.height &&
        previous.scrollHeight === next.scrollHeight &&
        previous.caret === next.caret &&
        previous.above === next.above &&
        previous.visible === next.visible
          ? previous
          : next,
      )
      layoutObserver?.disconnect()
      layoutObserver = null
      if (
        shown &&
        visible &&
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.right <= clientWidth &&
        rect.bottom <= clientHeight
      ) {
        const moved = () => {
          const current = anchor.getBoundingClientRect()
          return (
            Math.abs(current.top - rect.top) >= 1 ||
            Math.abs(current.right - rect.right) >= 1 ||
            Math.abs(current.bottom - rect.bottom) >= 1 ||
            Math.abs(current.left - rect.left) >= 1
          )
        }
        const observeMove = (threshold: number, padding = 0) => {
          if (disposed) return
          layoutObserver?.disconnect()
          let initial = true
          const margin = [rect.top, clientWidth - rect.right, clientHeight - rect.bottom, rect.left]
            .map(inset => `${padding - Math.max(0, Math.floor(inset))}px`)
            .join(' ')
          const nextObserver = new IntersectionObserver(
            entries => {
              if (disposed || layoutObserver !== nextObserver) return
              const ratio = entries[0]?.intersectionRatio
              if (ratio === undefined) return
              if (moved()) {
                refresh()
                return
              }
              if (initial) {
                if (ratio === 0 && padding === 0) observeMove(threshold, 2)
                else if (ratio > 0 && Math.abs(ratio - threshold) >= 0.01) observeMove(ratio, padding)
              }
              initial = false
            },
            {root: anchor.ownerDocument, threshold, rootMargin: margin},
          )
          layoutObserver = nextObserver
          nextObserver.observe(anchor)
        }
        observeMove(1)
      }
    }
    const refresh = () => {
      if (disposed || frame !== null) return
      frame = requestAnimationFrame(update)
    }
    refresh()
    const observer = new MutationObserver(refresh)
    observer.observe(anchor, {
      attributes: true,
      attributeFilter: ['aria-expanded', 'style', 'class', 'hidden', 'disabled'],
    })
    if (popoverRef.current) observer.observe(popoverRef.current, {childList: true, characterData: true, subtree: true})
    const {clippingAncestors, ancestors} = getPlacementBounds(
      portalSlot,
      anchor.ownerDocument.documentElement.clientWidth,
      anchor.ownerDocument.documentElement.clientHeight,
    )
    for (const ancestor of ancestors) {
      observer.observe(ancestor, {attributes: true, attributeFilter: ['class', 'style', 'data-dragging']})
    }
    const sizes = new Map<Element, {width: number; height: number}>()
    for (const element of [anchor, popoverRef.current]) {
      if (!element) continue
      const {width, height} = element.getBoundingClientRect()
      sizes.set(element, {width, height})
    }
    const resizeObserver = new ResizeObserver(entries => {
      let resized = false
      for (const {target} of entries) {
        const {width, height} = target.getBoundingClientRect()
        const previous = sizes.get(target)
        if (!previous || Math.abs(previous.width - width) >= 1 || Math.abs(previous.height - height) >= 1) {
          sizes.set(target, {width, height})
          resized = true
        }
      }
      if (resized) refresh()
    })
    resizeObserver.observe(anchor)
    if (popoverRef.current) resizeObserver.observe(popoverRef.current)
    for (const ancestor of clippingAncestors) resizeObserver.observe(ancestor)
    const intersectionObserver = new IntersectionObserver(
      entries => {
        const intersecting = (entries[0]?.intersectionRatio ?? 0) > 0
        if (anchorIntersectingRef.current !== intersecting) {
          anchorIntersectingRef.current = intersecting
          refresh()
        }
      },
      {root: anchor.ownerDocument},
    )
    intersectionObserver.observe(anchor)
    if (shown) {
      // eslint-disable-next-line github/prefer-observers -- observers do not report anchor movement from ancestor scrolling
      window.addEventListener('scroll', refresh, {capture: true})
      // eslint-disable-next-line github/prefer-observers -- ResizeObserver cannot observe the viewport the popover is positioned against
      window.addEventListener('resize', refresh)
    }
    return () => {
      disposed = true
      if (frame !== null) cancelAnimationFrame(frame)
      observer.disconnect()
      resizeObserver.disconnect()
      intersectionObserver.disconnect()
      layoutObserver?.disconnect()
      if (shown) {
        window.removeEventListener('scroll', refresh, true)
        window.removeEventListener('resize', refresh)
      }
    }
  }, [anchor, portalSlot, scope, shown])

  const restoreFocus = useCallback(
    (preventScroll = false) => {
      if (!focusedInsideRef.current) return
      const saved = focusReturnRef.current
      const target =
        saved?.isConnected && saved !== anchor.ownerDocument.body && !saved.matches(':disabled')
          ? saved
          : anchor.isConnected && !(anchor instanceof HTMLButtonElement && anchor.disabled)
            ? anchor
            : textAreaRef?.current?.isConnected && !textAreaRef.current.disabled
              ? textAreaRef.current
              : null
      if (preventScroll) target?.focus({preventScroll: true})
      else target?.focus()
    },
    [anchor, textAreaRef],
  )
  const restoreFocusRef = useRef(restoreFocus)
  const visibilityChangeRef = useRef(onVisibilityChange)
  useLayoutEffect(() => {
    if (previousAnchorRef.current !== anchor) {
      if (focusReturnRef.current && scope?.contains(focusReturnRef.current)) focusReturnRef.current = anchor
      previousAnchorRef.current = anchor
    }
    restoreFocusRef.current = restoreFocus
    visibilityChangeRef.current = onVisibilityChange
  }, [anchor, onVisibilityChange, restoreFocus, scope])

  useLayoutEffect(() => {
    if (!shown) return
    visibilityChangeRef.current?.(true)
    return () => {
      visibilityChangeRef.current?.(false)
      restoreFocusRef.current(!anchorIntersectingRef.current)
    }
  }, [shown])

  useEffect(() => {
    if (!shown) return
    const original = anchor.getAttribute('aria-describedby')
    anchor.setAttribute('aria-describedby', [original, descriptionId].filter(Boolean).join(' '))
    return () => {
      if (anchor.getAttribute('aria-describedby') !== [original, descriptionId].filter(Boolean).join(' ')) return
      if (original === null) anchor.removeAttribute('aria-describedby')
      else anchor.setAttribute('aria-describedby', original)
    }
  }, [anchor, descriptionId, shown])

  useEffect(() => {
    if (!shown) return
    const key = `${title}:${description}:${step?.index ?? ''}:${step?.count ?? ''}`
    if (announcedStepRef.current === key) return
    const firstRequestedFocus = focusRequested && announcedStepRef.current === null
    announcedStepRef.current = key
    if (firstRequestedFocus || focusedStepRef.current === key) return
    announce(`${title}. ${description}${step ? ` Step ${step.index + 1} of ${step.count}.` : ''}`, {element: anchor})
  }, [anchor, description, focusRequested, shown, step, title])

  useLayoutEffect(() => {
    const moved = previousPortalSlotRef.current !== portalSlot
    previousPortalSlotRef.current = portalSlot
    if (!shown) return
    const desired = stepFocusRef.current
    stepFocusRef.current = null
    if (!desired && !((moved && focusedInsideRef.current) || (focusRequested && !focusReturnRef.current))) return
    if (!focusReturnRef.current) {
      const active = anchor.ownerDocument.activeElement
      focusReturnRef.current =
        active instanceof HTMLElement && active !== anchor.ownerDocument.body && !popoverRef.current?.contains(active)
          ? active
          : anchor
    }
    const target = desired === 'previous' ? (previousRef.current ?? primaryRef.current) : primaryRef.current
    target?.focus()
    if (target && anchor.ownerDocument.activeElement === target) {
      focusedStepRef.current = `${title}:${description}:${step?.index ?? ''}:${step?.count ?? ''}`
    }
  }, [anchor, description, focusKey, focusRequested, portalSlot, shown, step?.count, step?.index, title])

  const style = {
    left: position?.left ?? 16,
    top: position?.top ?? 16,
    width: position?.width,
    visibility: shown ? 'visible' : 'hidden',
    transform: position?.above ? 'translateY(-100%)' : undefined,
    '--onboarding-caret-left': `${position?.caret ?? 24}px`,
    '--onboarding-width': `${position?.width ?? 320}px`,
    '--onboarding-height': `${position?.height ?? 320}px`,
  } as CSSProperties

  return createPortal(
    <Popover
      ref={popoverRef}
      open
      caret={position?.above ? 'bottom' : 'top'}
      className={styles.Popover}
      style={style}
      role="dialog"
      data-copilot-onboarding-popover=""
      aria-modal="false"
      aria-hidden={!shown}
      aria-labelledby={headingId}
      aria-describedby={`${descriptionId}${step ? ` ${counterId}` : ''}`}
    >
      <Popover.Content
        width="auto"
        className={clsx(styles.Content, appearance === 'announcement' && styles.Announcement)}
        data-announcement-paused={appearance === 'announcement' && !shown ? '' : undefined}
        onFocusCapture={event => {
          if (!focusReturnRef.current) {
            const previous = event.relatedTarget
            focusReturnRef.current =
              previous instanceof HTMLElement && previous !== anchor.ownerDocument.body ? previous : anchor
          }
          focusedInsideRef.current = true
          onFocusChange?.(true)
        }}
        onBlurCapture={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            focusedInsideRef.current = false
            onFocusChange?.(false)
          }
        }}
        onKeyDown={event => {
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- focused popover controls handle Escape before document-level dismissal
          if (!shown || event.key !== 'Escape' || event.defaultPrevented) return
          if (
            popoverRef.current &&
            event.target instanceof Element &&
            isInsideNestedOverlay(event.target, popoverRef.current, escapeScope?.())
          )
            return
          if (hasOpenOnboardingTooltip(anchor, popoverRef.current)) return
          event.preventDefault()
          event.stopPropagation()
          restoreFocus()
          onClose()
        }}
      >
        {shown && (
          <VisiblePopoverEscapeHandler
            anchor={anchor}
            popover={popoverRef}
            escapeScope={escapeScope}
            onClose={onClose}
            restoreFocus={restoreFocus}
          />
        )}
        <div ref={scrollableRef} className={styles.Scrollable} style={{height: position?.scrollHeight}}>
          <IconButton
            className={styles.Close}
            icon={XIcon}
            aria-label={closeLabel}
            variant="invisible"
            onClick={() => {
              restoreFocus()
              onClose()
            }}
          />
          {appearance === 'announcement' && (typeof media === 'function' ? media(shown) : media)}
          <h2 id={headingId} className={styles.Heading}>
            {title}
          </h2>
          <p id={descriptionId} className={styles.Description}>
            {description}
          </p>
          <div className={styles.Footer}>
            {step && (
              <>
                <span id={counterId} className={styles.Counter}>
                  Step {step.index + 1} of {step.count}
                </span>
                {step.onPrevious && (
                  <Button
                    ref={previousRef}
                    onClick={() => {
                      stepFocusRef.current = 'previous'
                      step.onPrevious?.()
                    }}
                  >
                    Previous
                  </Button>
                )}
                <Button
                  ref={primaryRef}
                  variant="primary"
                  onClick={() => {
                    if (step.index + 1 === step.count) restoreFocus()
                    else stepFocusRef.current = 'primary'
                    step.onNext()
                  }}
                >
                  {step.index + 1 === step.count ? 'Finish' : 'Next'}
                </Button>
              </>
            )}
            {actions?.map((action, index) => (
              <Button
                key={action.label}
                ref={index === actions.length - 1 ? primaryRef : undefined}
                variant={action.variant}
                onClick={() => {
                  restoreFocus()
                  action.onClick()
                }}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </Popover.Content>
    </Popover>,
    portalSlot,
  )
}
